import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Heart, Minus, Plus, ShoppingBag } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ProductCard } from "../components/ProductCard";
import { ProductReviews } from "../components/ProductReviews";
import { useCart, lineId } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useWishlist } from "../contexts/WishlistContext";
import { api, formatApiError, formatINR } from "../lib/api";
import { ProductImage } from "../components/ProductImage";
import { MediaGallery } from "../components/MediaGallery";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ids, toggle } = useWishlist();
  const { add, inc, dec, setOpen, qtyOf } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    setError("");
    setProduct(null);
    setSelected(null);
    api.get(`/products/${id}`)
      .then(async (response) => {
        setProduct(response.data);
        const activeVariants = (response.data.variants || []).filter((v) => v.active !== false);
        setSelected(activeVariants[0] || null);
        const relatedResponse = await api.get("/products", {
          params: { category: response.data.category },
        });
        setRelated(relatedResponse.data.filter((item) => item.id !== response.data.id).slice(0, 5));
      })
      .catch(() => setError("This product is unavailable right now."));
  }, [id]);

  const variants = useMemo(
    () => (product?.variants || []).filter((v) => v.active !== false),
    [product],
  );
  const price = selected ? Number(selected.price) : Number(product?.price || 0);
  const mrp = selected ? Number(selected.mrp || 0) : Number(product?.mrp || 0);
  const stock = selected ? Number(selected.stock) : Number(product?.stock || 0);
  const qty = qtyOf(product?.id, selected?.id);
  const lid = lineId(product?.id, selected?.id);
  const discount = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  const notifyMe = async () => {
    if (!user || user === false) {
      navigate(`/login?next=${encodeURIComponent(`/products/${id}`)}`);
      return;
    }
    try {
      await api.post(`/products/${product.id}/notify`, { variant_id: selected?.id || null });
      setNotified(true);
      toast.success("We'll notify you when it's back in stock");
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    }
  };

  const handleSave = async () => {
    if (!user || user === false) {
      navigate(`/login?next=${encodeURIComponent(`/products/${id}`)}`);
      return;
    }
    setBusy(true);
    try {
      const isSaved = await toggle(product.id);
      toast.success(isSaved ? "Saved to your wishlist" : "Removed from your wishlist");
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <main className="mx-auto grid min-h-[60vh] max-w-7xl place-items-center px-4 pb-28 md:px-6 md:pb-12">
        <div className="text-center" data-testid="product-detail-error">
          <p className="font-serif text-3xl text-stone-950">Product unavailable</p>
          <p className="mt-2 text-sm text-stone-600">{error}</p>
          <Link
            to="/categories"
            className="mt-5 inline-flex min-h-11 items-center bg-orange-600 px-4 text-sm font-semibold text-white"
            data-testid="product-error-back"
          >
            Browse categories
          </Link>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-7xl px-4 pb-28 pt-8 md:px-6 md:pb-12" data-testid="product-detail-loading">
        <div className="h-6 w-28 animate-pulse bg-stone-100" />
        <div className="mt-6 grid gap-8 md:grid-cols-2">
          <div className="aspect-square animate-pulse bg-stone-100" />
          <div className="h-80 animate-pulse bg-stone-100" />
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 md:px-6 md:pb-12">
      <Link
        to={`/c/${product.category}`}
        className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-stone-700 hover:text-orange-700"
        data-testid="product-detail-back"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to {product.category.replace(/_/g, " ")}
      </Link>

      <section className="mt-5 grid gap-8 md:grid-cols-[minmax(0,1fr)_minmax(320px,0.8fr)] md:gap-14">
        <div className="md:sticky md:top-24 md:self-start">
          <MediaGallery
            images={(product.images && product.images.length) ? product.images : [product.image].filter(Boolean)}
            video={product.video}
            name={product.name}
            discount={discount}
          />
        </div>

        <div className="flex flex-col justify-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-700">
            {product.category.replace(/_/g, " ")}
          </p>
          <h1 className="mt-3 font-serif text-4xl leading-none text-stone-950 md:text-5xl" data-testid="product-detail-name">
            {product.name}
          </h1>
          {variants.length > 0 ? (
            <div className="mt-5" data-testid="product-detail-variants">
              <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Choose an option</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {variants.map((v) => {
                  const soldOut = Number(v.stock) <= 0;
                  const isSel = selected?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      disabled={soldOut}
                      onClick={() => { setSelected(v); setNotified(false); }}
                      data-testid={`product-variant-${v.id}`}
                      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                        isSel
                          ? "border-orange-600 bg-orange-50 text-orange-700"
                          : "border-stone-300 bg-white text-stone-700 hover:border-orange-300"
                      } ${soldOut ? "cursor-not-allowed opacity-40" : ""}`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="mt-3 text-sm text-stone-500" data-testid="product-detail-unit">{product.unit}</p>
          )}
          <div className="mt-6 flex items-end gap-3">
            <p className="text-3xl font-semibold text-stone-950" data-testid="product-detail-price">
              {formatINR(price)}
            </p>
            {discount > 0 && (
              <>
                <p className="pb-1 text-sm text-stone-400 line-through">{formatINR(mrp)}</p>
                <p className="pb-1 text-sm font-semibold text-green-700">{discount}% off</p>
              </>
            )}
          </div>
          <p
            className={`mt-5 inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold ${
              stock > 0 ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
            }`}
            data-testid="product-detail-stock"
          >
            {stock > 0 ? "In stock" : "Unavailable"}
          </p>
          {product.description && <p className="mt-5 text-sm leading-6 text-stone-600">{product.description}</p>}

          <div className="mt-8 flex flex-wrap gap-3">
            {qty > 0 ? (
              <div
                className="inline-flex min-h-12 items-center rounded-full border border-orange-600 bg-orange-50"
                data-testid="product-detail-quantity"
              >
                <button
                  type="button"
                  onClick={() => dec(lid)}
                  className="grid h-12 w-12 place-items-center text-orange-700 hover:bg-orange-100"
                  data-testid="product-detail-decrease"
                  aria-label="Decrease quantity"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="min-w-10 text-center text-sm font-bold text-stone-950" data-testid="product-detail-quantity-value">
                  {qty}
                </span>
                <button
                  type="button"
                  onClick={() => inc(lid)}
                  className="grid h-12 w-12 place-items-center text-orange-700 hover:bg-orange-100"
                  data-testid="product-detail-increase"
                  aria-label="Increase quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            ) : stock <= 0 ? (
              <button
                type="button"
                onClick={notifyMe}
                disabled={notified}
                className={`inline-flex min-h-12 items-center gap-2 rounded-full border px-5 text-sm font-semibold transition-colors ${
                  notified
                    ? "border-green-300 bg-green-50 text-green-700"
                    : "border-stone-300 bg-white text-stone-700 hover:border-orange-400 hover:text-orange-700"
                }`}
                data-testid="product-detail-notify"
              >
                {notified ? "You'll be notified ✓" : "Notify me when available"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => add(product, selected)}
                className="inline-flex min-h-12 items-center gap-2 rounded-full bg-orange-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-orange-700"
                data-testid="product-detail-add"
              >
                <ShoppingBag className="h-4 w-4" />
                Add to cart
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={handleSave}
              className={`grid h-12 w-12 place-items-center rounded-full border transition-colors ${
                ids.includes(product.id)
                  ? "border-orange-600 bg-orange-50 text-orange-700"
                  : "border-stone-300 bg-white text-stone-700 hover:border-orange-300"
              }`}
              data-testid="product-detail-wishlist"
              aria-label={ids.includes(product.id) ? "Remove from wishlist" : "Save to wishlist"}
            >
              <Heart className="h-5 w-5" fill={ids.includes(product.id) ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="min-h-12 rounded-full border border-stone-950 px-5 text-sm font-semibold text-stone-950 transition-colors hover:bg-stone-950 hover:text-white"
              data-testid="product-detail-buy"
            >
              View cart
            </button>
          </div>
        </div>
      </section>

      <ProductReviews productId={product.id} />

      {/* Sticky mobile purchase bar */}
      <div
        className="fixed inset-x-0 bottom-20 z-[999] flex items-center gap-3 border-t border-stone-200 bg-white p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] md:hidden"
        data-testid="product-sticky-bar"
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs text-stone-500">{selected?.label || product.unit}</p>
          <p className="text-lg font-extrabold text-stone-900">{formatINR(price)}</p>
        </div>
        {qty > 0 ? (
          <div className="flex min-h-11 items-center rounded-full border border-[#FF5200] bg-orange-50">
            <button type="button" onClick={() => dec(lid)} className="grid h-11 w-11 place-items-center text-[#FF5200]" data-testid="sticky-decrease"><Minus className="h-4 w-4" /></button>
            <span className="min-w-8 text-center text-sm font-bold" data-testid="sticky-qty">{qty}</span>
            <button type="button" onClick={() => inc(lid)} className="grid h-11 w-11 place-items-center text-[#FF5200]" data-testid="sticky-increase"><Plus className="h-4 w-4" /></button>
          </div>
        ) : stock <= 0 ? (
          <button type="button" onClick={notifyMe} disabled={notified} className="min-h-11 rounded-full border border-stone-300 px-5 text-sm font-bold text-stone-700" data-testid="sticky-notify">
            {notified ? "Notified ✓" : "Notify me"}
          </button>
        ) : (
          <button type="button" onClick={() => add(product, selected)} className="min-h-11 rounded-full bg-[#FF5200] px-6 text-sm font-extrabold text-white active:scale-95" data-testid="sticky-add-to-cart">
            Add to Cart
          </button>
        )}
      </div>

      {related.length > 0 && (
        <section className="mt-14 border-t border-stone-200 pt-8">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-700">More to explore</p>
          <h2 className="mt-2 font-serif text-3xl text-stone-950">Related picks</h2>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
            {related.map((item) => <ProductCard key={item.id} product={item} />)}
          </div>
        </section>
      )}
    </main>
  );
}
